import { oc } from '@orpc/contract';
import { z } from 'zod';
import { Email, Id, OkResponse, Password, Role, Timestamp } from './common.js';
export const RegistrationMode = z.enum(['invite', 'open']);
export const InviteStatus = z.enum(['valid', 'used', 'expired', 'revoked']);
export const UserSummary = z.object({
    id: Id,
    name: z.string(),
    email: z.string(),
    role: Role,
    createdAt: Timestamp,
});
export const InviteSummary = z.object({
    id: Id,
    email: z.string().nullable(),
    role: Role,
    status: InviteStatus,
    createdAt: Timestamp,
    expiresAt: Timestamp,
    usedByEmail: z.string().nullable(),
});
export const AcceptInviteInput = z.object({
    token: z.string().min(16).max(128),
    name: z.string().trim().min(1).max(100),
    email: Email,
    password: Password,
});
export const CreateInviteInput = z.object({
    email: Email.optional(),
    role: Role.default('user'),
    ttlDays: z.number().int().min(1).max(90).default(7),
});
const inviteErrors = {
    INVITE_INVALID: { status: 400, message: 'invite_invalid' },
    EMAIL_MISMATCH: { status: 400, message: 'email_mismatch' },
    EMAIL_TAKEN: { status: 409, message: 'email_taken' },
};
export const invitesContract = {
    /** Публично: что за приглашение (для экрана регистрации). Неизвестный токен — INVITE_INVALID. */
    inspect: oc
        .route({ method: 'GET', path: '/invites/{token}' })
        .errors(inviteErrors)
        .input(z.object({ token: z.string().min(16).max(128) }))
        .output(z.object({ status: InviteStatus, email: z.string().nullable(), role: Role })),
    /** Публично: регистрация по приглашению. После успеха клиент входит обычным sign-in. */
    accept: oc
        .route({ method: 'POST', path: '/invites/accept' })
        .errors(inviteErrors)
        .input(AcceptInviteInput)
        .output(z.object({ userId: Id })),
};
export const adminContract = {
    users: {
        list: oc.route({ method: 'GET', path: '/admin/users' }).output(z.array(UserSummary)),
    },
    invites: {
        list: oc.route({ method: 'GET', path: '/admin/invites' }).output(z.array(InviteSummary)),
        create: oc
            .route({ method: 'POST', path: '/admin/invites' })
            .input(CreateInviteInput)
            .output(z.object({ invite: InviteSummary, url: z.url() })),
        revoke: oc
            .route({ method: 'DELETE', path: '/admin/invites/{id}' })
            .input(z.object({ id: Id }))
            .output(OkResponse),
    },
    settings: {
        get: oc
            .route({ method: 'GET', path: '/admin/settings' })
            .output(z.object({ registrationMode: RegistrationMode, mailConfigured: z.boolean() })),
        update: oc
            .route({ method: 'PATCH', path: '/admin/settings' })
            .input(z.object({ registrationMode: RegistrationMode }))
            .output(OkResponse),
    },
};
/** Текущая сессия в том виде, в каком её видит UI (не зависит от библиотеки auth). */
export const SessionInfo = z.object({
    user: z.object({ id: Id, name: z.string(), email: z.string(), role: Role }),
    session: z.object({ id: Id, expiresAt: Timestamp }),
});
/** Активная сессия пользователя (устройство) для экрана «Устройства». */
export const DeviceSession = z.object({
    id: Id,
    token: z.string(),
    userAgent: z.string().nullable(),
    ipAddress: z.string().nullable(),
    createdAt: Timestamp,
    expiresAt: Timestamp,
    current: z.boolean(),
});
export const SignInInput = z.object({
    email: Email,
    password: z.string().min(1).max(128),
});
export const ResetPasswordInput = z.object({
    token: z.string().min(1),
    newPassword: Password,
});
//# sourceMappingURL=auth.js.map